import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { httpBatchLink } from "@trpc/client"
import { useState } from "react"
import superjson from "superjson"
import { Provider as UrqlProvider } from "urql"
import { TranslationProvider } from "~/locale/TranslationProvider"
import { Root } from "~/modules/main"
import { theme } from "~/theme"
import { PromptProvider, UrThemeProvider } from "~/ur-react-components"
import { client } from "~/urqlClient"
import ToastProvider from "./containers/ToastProvider"
import { getAuthToken } from "./modules/authentication/util"
import { API_URL } from "./utils/env"
import { trpc } from "./utils/trpc"

const App: React.FC = () => {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // Don't retry on 401 errors
          if ((error as any)?.data?.httpStatus === 401) {
            return false
          }
          // Otherwise retry up to 3 times
          return failureCount < 3
        },
      },
    },
  }))
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: `${API_URL}/trpc`,
          transformer: superjson as any,
          headers() {
            const authToken = getAuthToken()
            return {
              authorization: authToken ? `Bearer ${authToken}` : "",
            }
          },
        }),
      ],
    }),
  )

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <UrqlProvider value={client}>
        <TranslationProvider>
          <UrThemeProvider theme={theme}>
            <ToastProvider>
              <PromptProvider>
                <Root />
              </PromptProvider>
            </ToastProvider>
          </UrThemeProvider>
        </TranslationProvider>
      </UrqlProvider>
    </trpc.Provider>
  )
}

export default App
